import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { CustomPortal, MembershipModal } from '@smuchile/pkg-unimarc-components'
import {
  Cookies,
  datalayerSimpleEvent,
  getCurrentDomain,
  getParamsInURL,
  trigger,
  useEvents,
  useMembershipData,
  useMobile,
  useUserWithMembership
} from '@smuchile/pkg-unimarc-hooks'
import { IMembershipResponse } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/IMembership'
import { ToastWrapper } from 'components/ToastWrapper'
import { dataLayerMembership } from './helpers/dataLayerMembership'
import { FIVE_MINUTES_OUTDATED_INFO } from '@smuchile/pkg-unimarc-hooks/shared/constants'

export const MembershipWrapper = () => {
  const domain = getCurrentDomain()
  const { isMobile } = useMobile()
  const router = useRouter()
  // comes from checkout to change membership card
  const changeMembershipCard = Boolean(getParamsInURL('changeMembershipCard'))
  const [runReload, setRunReload] = useState(false)
  const [membershipResponse, setMembershipResponse] =
    useState<IMembershipResponse>()
  const [eventContext, setEventContext] = useState('')

  const { data } = useUserWithMembership({
    staleTime: FIVE_MINUTES_OUTDATED_INFO
  })

  const {
    animationFiles,
    availableCards,
    coconutImg,
    dataHasFailed,
    handleChooseCard,
    handleCloseMembershipModal,
    handleGetMembership,
    handleOpenMembershipModal,
    handleRefreshData,
    handleSelectPaymentMethod,
    handleSelectPlan,
    handleSetCard,
    handleStopAnimation,
    handleToggleTerms,
    handleToggleModalCards,
    handleToggleModalListPaymentMethods,
    isLoading,
    isLoadingModalCards,
    isLoadingTrialTime,
    isLoadingANewCard,
    labelBtn,
    membershipData,
    membershipStatus,
    modalTitle,
    openMembershipModal,
    openModalCards,
    openModalListPaymentMethods,
    planSelected,
    selectedCard,
    selectedTemporalCard,
    toggleTerms
  } = useMembershipData(changeMembershipCard)

  const handleMembership = () => {
    if (eventContext) {
      const dataLayer = dataLayerMembership({
        status: data?.membership,
        eventContext,
        planSelected,
        type: 'button'
      })
      datalayerSimpleEvent({ ...dataLayer })
    }
    handleGetMembership()
  }

  useEvents({
    eventType: 'openMembershipModal',
    callBack: ({ detail }) => {
      handleOpenMembershipModal()
      if (detail?.openPaymentMehtods) {
        handleToggleModalCards({ shouldShowMemberShipModal: false })
      } else {
        setEventContext(detail?.eventContext)
        if (detail?.eventContext) {
          const dataLayer = dataLayerMembership({
            status: data?.membership,
            eventContext: detail?.eventContext,
            planSelected,
            type: 'open'
          })
          datalayerSimpleEvent({ ...dataLayer })
        }
        handleOpenMembershipModal()
      }
    }
  })

  useEvents({
    eventType: 'membershipToast',
    callBack: ({ detail }) => {
      if (changeMembershipCard) {
        // show a toast that card was changed successfully
        setMembershipResponse({ ...detail, animationFinished: true })

        // clear changeMembershipCard param from url
        const { pathname } = router
        const params = new URLSearchParams(window.location.search)
        params.delete('changeMembershipCard')
        return router.replace({ pathname, query: params.toString() })
      } else if (detail?.success) {
        Cookies.set(
          'showMembershipToast',
          JSON.stringify({ ...detail, animationFinished: true }),
          {
            domain
          }
        )
        setRunReload(true)
      }
      return setMembershipResponse(detail)
    }
  })

  const handleCloseToast = () => {
    trigger({
      eventType: 'membershipToast',
      data: {
        showTooltip: false
      }
    })
    if (membershipResponse?.success) {
      Cookies.remove('showMembershipToast', {
        domain
      })
    }
  }

  useEffect(() => {
    if (!isLoadingTrialTime) {
      setMembershipResponse({
        ...membershipResponse,
        animationFinished: true
      })
      if (runReload) {
        router.reload()
      }
    }
  }, [isLoadingTrialTime])

  useEffect(() => {
    const showToast = JSON.parse(Cookies.get('showMembershipToast') ?? '{}')
    if (showToast?.showTooltip) {
      setMembershipResponse(showToast)
    }
  }, [])

  return (
    <>
      {membershipResponse?.animationFinished &&
        membershipResponse?.showTooltip &&
        !runReload && (
          <CustomPortal
            rootId={membershipResponse?.success ? '' : 'portal-membership'}
          >
            <ToastWrapper
              errorCode={membershipResponse?.errorCode}
              eventContext={eventContext}
              membershipStatus={data?.membership}
              message={membershipResponse?.message}
              onClose={handleCloseToast}
              planSelected={planSelected}
              success={membershipResponse?.success}
            />
          </CustomPortal>
        )}
      {openMembershipModal && (
        <MembershipModal
          animationFiles={animationFiles}
          coconutImg={coconutImg}
          dataHasFailed={dataHasFailed}
          handleClose={handleCloseMembershipModal}
          handleGetMembership={handleMembership}
          handleRefreshData={handleRefreshData}
          handleSelectPlan={handleSelectPlan}
          handleStopAnimation={handleStopAnimation}
          handleToggleTerms={handleToggleTerms}
          isLoading={isLoading}
          isLoadingTrialTime={isLoadingTrialTime}
          isOpenModalCards={openModalCards}
          isOpenModalPaymentMethods={openModalListPaymentMethods}
          labelBtn={labelBtn}
          membershipData={membershipData}
          membershipResponse={membershipResponse}
          membershipStatus={membershipStatus}
          modalListNewPaymentsProps={{
            handleGoBack: handleToggleModalCards,
            handleSelectPaymentMethod: handleSelectPaymentMethod,
            handleToggle:
              !openModalListPaymentMethods || isMobile
                ? handleToggleModalListPaymentMethods
                : handleCloseMembershipModal,
            paymentMethods: membershipData?.modalAvailablePaymentMethods,
            total: planSelected?.subtitulo2 as string,
            isLoading: isLoadingANewCard
          }}
          modalListPaymentsCardsProps={{
            cardsToList: availableCards,
            handleContinue: handleSetCard,
            handleSelectCard: handleChooseCard,
            handleToggle:
              !openModalCards || isMobile
                ? handleToggleModalCards
                : handleCloseMembershipModal,
            handleTooglePaymentMetods: handleToggleModalListPaymentMethods,
            isLoading: isLoadingModalCards,
            selectedCard: selectedTemporalCard,
            total: planSelected?.subtitulo2 as string
          }}
          modalTitle={modalTitle}
          planSelected={planSelected}
          selectedCard={selectedCard}
          toggleTerms={toggleTerms}
        />
      )}
    </>
  )
}
